import { NAMESPACE, VERSION } from "@/constants/documentModel";
import logger from "@/util/logger";

class DocumentModel {
    constructor() {
        this.contentControls = [];
        this.businessRules = [];
    }

    __findInContentControls(container, predicate, then = null) {
        let results = [];
        const { contentControls } = container;

        for (let i = 0; i < contentControls.length; i++) {
            const contentControl = contentControls[i];
            if (predicate(contentControl)) {
                results.push(contentControl);
                if (then) {
                    then({ contentControl, container, index: contentControls.indexOf(contentControl) });
                }
            }

            results = [...results, ...this.__findInContentControls({ parent: contentControl, contentControls: contentControl.contentControls }, predicate, then)];
        }

        return results;
    }

    findContentControl(id) {
        const results = this.__findInContentControls({ contentControls: this.contentControls }, c => c.id === id);
        if (results.length > 0) {
            return results[0];
        }

        return null;
    }

    deleteContentControl(id) {
        this.__findInContentControls(
            { contentControls: this.contentControls },
            c => c.id === id,
            ({ container, index }) => {
                container.contentControls.splice(index, 1);

                // Update 12/09/2024:  Looks like Microsoft have now changed this behaviour in the latest version of Word.
                // Leaving this in for the time-being as an aide memoire.

                // Word will automatically delete any outer content controls that no longer have any children.
                // Therefore we scan back up the tree and delete any empty containers to keep the model consistent with the document.
                /*if (container.parent && container.contentControls.length == 0) {
                    this.deleteContentControl(container.parent.id);
                }*/
            });
    }

    findContentControlByType(type) {
        return this.__findInContentControls({ contentControls: this.contentControls }, c => c.type === type);
    }

    findContentControlBySystemName(systemName) {
        return this.__findInContentControls({ contentControls: this.contentControls }, c => c.systemName === systemName);
    }

    businessRuleInUse(id) {
        const results = this.__findInContentControls({ contentControls: this.contentControls }, c => c.displayRuleId === id);
        return results.length > 0;
    }

    __populateContentControlList(contentControl, list) {
        if (contentControl.contentControls) {
            for (let i = 0; i < contentControl.contentControls.length; i++) {
                this.__populateContentControlList(contentControl.contentControls[i], list);
            }
        }

        list.push(contentControl);
    }

    allContentControls() {
        const list = [];

        for (let i = 0; i < this.contentControls.length; i++) {
            this.__populateContentControlList(this.contentControls[i], list);
        }

        return list;
    }
}

const create = () => new DocumentModel();

const buildContentControlModel = (contentControlElem) => {
    logger.debug("Building model for content control.")

    const id = parseInt(contentControlElem.getAttribute("contentControlId"));
    const type = contentControlElem.getElementsByTagName("type")[0].textContent;
    const systemName = contentControlElem.getElementsByTagName("systemName")[0].textContent;
    const displayRuleId = contentControlElem.getElementsByTagName("displayRuleId")[0].textContent;

    const labelElem = contentControlElem.getElementsByTagName("label");
    let label = "";
    if (labelElem && labelElem.length > 0) {
        label = labelElem[0].textContent;
    }

    const versionElem = contentControlElem.getElementsByTagName("version");
    let version = null;
    if (versionElem && versionElem.length > 0) {
        version = {
            number: parseInt(versionElem[0].textContent),
            date: versionElem[0].getAttribute("date")
        }
    }

    const model = { id, type, systemName, label, version, displayRuleId, contentControls: [] };

    const childContentControls = contentControlElem.getElementsByTagName("contentControls");
    if (childContentControls && childContentControls.length > 0) {
        const contentControls = childContentControls[0].children;
        for (let i = 0; i < contentControls.length; i++) {
            const contentControl = contentControls[i];
            const contentControlModel = buildContentControlModel(contentControl, model.contentControls);

            model.contentControls.push(contentControlModel);
        }
    }

    logger.debug("Built model for content control.", model);

    return model;
}

const buildModel = xml => {
    logger.debug("Building document model from XML.", { xml });
    const model = create();

    if (xml) {
        const parser = new DOMParser();
        const deserialized = parser.parseFromString(xml, "application/xml");
        const root = deserialized.documentElement;

        const contentControlsCollection = root.getElementsByTagName("contentControls");
        if (contentControlsCollection && contentControlsCollection.length > 0) {
            const contentControls = contentControlsCollection[0].children;
            for (let i = 0; i < contentControls.length; i++) {
                const contentControl = contentControls[i];
                const contentControlModel = buildContentControlModel(contentControl, model.contentControls);

                model.contentControls.push(contentControlModel);
            }
        }

        const businessRulesCollection = root.getElementsByTagName("businessRules");
        if (businessRulesCollection && businessRulesCollection.length > 0) {
            logger.debug("Building model for display rules.");

            const businessRules = businessRulesCollection[0].children;
            for (let i = 0; i < businessRules.length; i++) {
                const businessRule = businessRules[i];

                const id = businessRule.getAttribute("id");
                const name = businessRule.getElementsByTagName("name")[0].textContent;
                const expression = businessRule.getElementsByTagName("expression")[0].textContent;

                const businessRuleModel = { id, name, expression };

                logger.debug("Built model for display rule.", { model: businessRuleModel });

                model.businessRules.push(businessRuleModel);
            }
        }
    }

    logger.debug("Built document from XML.", { xml, model });
    return model;
};

const buildContentControlXml = (contentControl, xmlDoc, collectionElem) => {
    logger.debug("Building XML for content control.", { contentControl, xmlDoc });

    const contentControlElem = xmlDoc.createElementNS(NAMESPACE, "contentControl");
    contentControlElem.setAttribute("contentControlId", contentControl.id);
    collectionElem.append(contentControlElem);

    const typeElem = xmlDoc.createElementNS(NAMESPACE, "type");
    typeElem.append(contentControl.type);
    contentControlElem.append(typeElem);

    const systemNameElem = xmlDoc.createElementNS(NAMESPACE, "systemName");
    systemNameElem.append(contentControl.systemName);
    contentControlElem.append(systemNameElem);

    if (contentControl.label) {
        const labelElem = xmlDoc.createElementNS(NAMESPACE, "label");
        labelElem.append(contentControl.label);
        contentControlElem.append(labelElem);
    }

    if (contentControl.version) {
        const versionElem = xmlDoc.createElementNS(NAMESPACE, "version");
        versionElem.setAttribute("date", contentControl.version.date);
        versionElem.append(contentControl.version.number);
        contentControlElem.append(versionElem);
    }

    const displayRuleElem = xmlDoc.createElementNS(NAMESPACE, "displayRuleId");
    displayRuleElem.append(contentControl.displayRuleId);
    contentControlElem.append(displayRuleElem);

    const childContentControlsElem = xmlDoc.createElementNS(NAMESPACE, "contentControls");
    contentControlElem.append(childContentControlsElem);

    for (let i = 0; i < contentControl.contentControls.length; i++) {
        const childContentControl = contentControl.contentControls[i];
        buildContentControlXml(childContentControl, xmlDoc, childContentControlsElem);
    }

    logger.debug("Built XML for content control.", { contentControl, xmlDoc });
}

const buildXml = model => {
    logger.debug("Building custom XML.", model);

    const xmlDoc = new DOMParser().parseFromString(`<sharedo.autodoc version="${VERSION}" xmlns="${NAMESPACE}"></sharedo.autodoc>`, "application/xml");
    const root = xmlDoc.documentElement;

    const contentControlsCollection = xmlDoc.createElementNS(NAMESPACE, "contentControls");
    root.append(contentControlsCollection);

    for (let i = 0; i < model.contentControls.length; i++) {
        const contentControl = model.contentControls[i];
        buildContentControlXml(contentControl, xmlDoc, contentControlsCollection);
    }

    const businessRulesCollection = xmlDoc.createElementNS(NAMESPACE, "businessRules");
    root.append(businessRulesCollection);

    for (let i = 0; i < model.businessRules.length; i++) {
        const rule = model.businessRules[i];

        const businessRuleElem = xmlDoc.createElementNS(NAMESPACE, "businessRule");
        businessRuleElem.setAttribute("id", rule.id);
        businessRulesCollection.append(businessRuleElem);

        const nameElem = xmlDoc.createElementNS(NAMESPACE, "name");
        nameElem.append(rule.name);
        businessRuleElem.append(nameElem);

        const exprElem = xmlDoc.createElementNS(NAMESPACE, "expression");
        exprElem.append(rule.expression);
        businessRuleElem.append(exprElem);
    }

    const xml = new XMLSerializer().serializeToString(xmlDoc);

    logger.debug("Built custom XML.", { model, xml });
    return xml;
}

const get = () => new Promise(resolve => {
    if (window.Office.context.document) {
        logger.debug("Loading document model from XML.")
        window.Office.context.document.customXmlParts.getByNamespaceAsync(NAMESPACE, partResult => {
            if (partResult.status === window.Office.AsyncResultStatus.Succeeded) {
                const parts = partResult.value;

                if (parts.length > 0) {
                    parts[0].getXmlAsync(xmlResult => {
                        if (xmlResult.status === window.Office.AsyncResultStatus.Succeeded) {
                            const xml = xmlResult.value;

                            logger.debug("Metadata XML found in the document.", { xml });
                            const model = buildModel(xml);

                            resolve(model);
                        } else {
                            logger.debug("No metadata XML found in the document.");
                            resolve(null);
                        }
                    });
                } else {
                    logger.debug("No metadata XML found in the document.");
                    resolve(null);
                }
            } else {
                logger.debug("No metadata XML found in the document.");
                resolve(null);
            }
        });
    } else {
        resolve(null);
    }
});

const addNewCustomXml = model => new Promise((resolve, reject) => {
    const xml = buildXml(model);

    window.Office.context.document.customXmlParts.addAsync(xml, addResult => {
        if (addResult.status === window.Office.AsyncResultStatus.Succeeded) {
            resolve();
        } else {
            reject("Unable to add new custom metadata to the document");
        }
    })
})

const save = model => new Promise((resolve, reject) => {
    if (window.Office.context.document) {
        logger.debug("Saving document model.", model);
        window.Office.context.document.customXmlParts.getByNamespaceAsync(NAMESPACE, oldPartResult => {
            if (oldPartResult.status === window.Office.AsyncResultStatus.Succeeded && oldPartResult.value.length > 0) {
                oldPartResult.value[0].deleteAsync(deleteResult => {
                    if (deleteResult.status === window.Office.AsyncResultStatus.Succeeded) {
                        addNewCustomXml(model).then(() => resolve()).catch(error => reject(error));
                    } else {
                        const message = "Unable to delete old custom metadata from the document."
                        logger.error(message);
                        reject(message);
                    }
                });
            } else {
                addNewCustomXml(model).then(() => resolve()).catch(error => reject(error));
            }
        });
    } else {
        resolve();
    }
})

export default {
    create,
    get,
    save
}